import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Rendered instead of the children once one of them fails to render. */
  fallback?: ReactNode;
  /** The children get another chance whenever this value changes (the current location, for example). */
  resetKey?: unknown;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  public componentDidUpdate(prevProps: Props, prevState: State): void {
    const { hasError } = this.state;
    const { resetKey } = this.props;
    // The update that caught the error is left alone: the key may have changed in that very render.
    if (hasError && prevState.hasError && prevProps.resetKey !== resetKey) {
      this.setState({ hasError: false });
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('error: ', error);
    console.error('errorInfo:  ', JSON.stringify(errorInfo));
    console.error('componentStack: ', errorInfo.componentStack);
  }

  public render(): ReactNode {
    const { hasError } = this.state;
    const { children, fallback } = this.props;
    if (hasError) {
      return fallback ?? <>Error</>;
    }

    return children;
  }
}
